import React from 'react';
import {
    computeInitialCropRect,
    constrainCropPosition,
    cropImageToDataUrl,
    dataUrlToBase64,
    DEFAULT_CROP_SIZE,
    getCropAspectRatio,
    getCropNaturalDimensions,
    loadImageSize,
    readFileAsDataUrl,
    resizeCropRect,
    validateImageFile,
    type CropRect,
    type CropSize,
    type ImageCropUploadValue,
    type ImageUploadPayload,
    type Size,
} from './imageCropUploadHelpers';

type UploadResult = {
    imageName: string;
    imageUrl: string;
};

type UseImageCropUploadParams = {
    cropSize?: CropSize;
    mode: 'deferred' | 'immediate';
    onChange: (value: ImageCropUploadValue | null) => void;
    onPendingCropChange?: (pending: boolean) => void;
    onUpload?: (payload: ImageUploadPayload) => Promise<UploadResult>;
};

type Selection = {
    dataUrl: string;
    fileName: string;
    mimeType: string;
    naturalSize: Size;
    size: number;
};

const EMPTY_SIZE: Size = { width: 0, height: 0 };

export const useImageCropUpload = ({
    cropSize = DEFAULT_CROP_SIZE,
    mode,
    onChange,
    onPendingCropChange,
    onUpload,
}: UseImageCropUploadParams) => {
    const aspectRatio = getCropAspectRatio(cropSize);

    const [selection, setSelection] = React.useState<Selection | null>(null);
    const [displayedSize, setDisplayedSize] = React.useState<Size>(EMPTY_SIZE);
    const [cropRect, setCropRect] = React.useState<CropRect | null>(null);
    const [error, setError] = React.useState<
        'type' | 'size' | 'crop' | 'read' | null
    >(null);
    const [isApplying, setIsApplying] = React.useState(false);

    const dragState = React.useRef<{
        pointerId: number;
        mode: 'move' | 'resize';
        startX: number;
        startY: number;
        origin: CropRect;
    } | null>(null);

    React.useEffect(() => {
        onPendingCropChange?.(selection !== null);
    }, [onPendingCropChange, selection]);

    const acceptFile = React.useCallback(async (file: File) => {
        const validation = validateImageFile(file);
        if (!validation.ok) {
            setError(validation.reason);
            return;
        }

        setError(null);

        try {
            const dataUrl = await readFileAsDataUrl(file);
            const naturalSize = await loadImageSize(dataUrl);

            setDisplayedSize(EMPTY_SIZE);
            setCropRect(null);
            setSelection({
                dataUrl,
                fileName: file.name,
                mimeType: file.type,
                naturalSize,
                size: file.size,
            });
        } catch {
            setError('read');
        }
    }, []);

    const onSelectFiles = React.useCallback(
        (files: FileList | File[]) => {
            const file = Array.from(files)[0];
            if (file) {
                void acceptFile(file);
            }
        },
        [acceptFile],
    );

    const onImageLoad = React.useCallback(
        (size: Size) => {
            setDisplayedSize(size);
            setCropRect(computeInitialCropRect(size, aspectRatio));
        },
        [aspectRatio],
    );

    const onCropPointerDown = React.useCallback(
        (event: React.PointerEvent, handle: 'move' | 'resize') => {
            if (!cropRect) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            (event.target as Element).setPointerCapture(event.pointerId);
            dragState.current = {
                pointerId: event.pointerId,
                mode: handle,
                startX: event.clientX,
                startY: event.clientY,
                origin: cropRect,
            };
        },
        [cropRect],
    );

    const onCropPointerMove = React.useCallback(
        (event: React.PointerEvent) => {
            const drag = dragState.current;
            if (!drag || drag.pointerId !== event.pointerId) {
                return;
            }

            const deltaX = event.clientX - drag.startX;
            const deltaY = event.clientY - drag.startY;

            if (drag.mode === 'move') {
                setCropRect(
                    constrainCropPosition(
                        {
                            ...drag.origin,
                            x: drag.origin.x + deltaX,
                            y: drag.origin.y + deltaY,
                        },
                        displayedSize,
                    ),
                );
                return;
            }

            setCropRect(
                resizeCropRect(
                    drag.origin,
                    drag.origin.width + deltaX,
                    displayedSize,
                    aspectRatio,
                ),
            );
        },
        [aspectRatio, displayedSize],
    );

    const onCropPointerUp = React.useCallback((event: React.PointerEvent) => {
        const drag = dragState.current;
        if (drag && drag.pointerId === event.pointerId) {
            dragState.current = null;
        }
    }, []);

    const cancelSelection = React.useCallback(() => {
        setSelection(null);
        setCropRect(null);
        setDisplayedSize(EMPTY_SIZE);
        setError(null);
    }, []);

    const onRemove = React.useCallback(() => {
        cancelSelection();
        onChange(null);
    }, [cancelSelection, onChange]);

    const onApplyCrop = React.useCallback(async () => {
        if (!selection || !cropRect || displayedSize.width === 0) {
            return;
        }

        setIsApplying(true);
        setError(null);

        try {
            const dataUrl = await cropImageToDataUrl({
                source: selection.dataUrl,
                rect: cropRect,
                displayedSize,
                naturalSize: selection.naturalSize,
                cropSize,
                mimeType: selection.mimeType,
            });

            const payload: ImageUploadPayload = {
                dataBase64: dataUrlToBase64(dataUrl),
                fileName: selection.fileName,
                mimeType: selection.mimeType,
                size: selection.size,
            };

            if (mode === 'immediate') {
                if (!onUpload) {
                    throw new Error('onUpload-required');
                }
                const result = await onUpload(payload);
                onChange({
                    imageName: result.imageName,
                    imageUrl: result.imageUrl,
                });
            } else {
                onChange({
                    imageName: selection.fileName,
                    imageUrl: dataUrl,
                    imageUpload: payload,
                });
            }

            cancelSelection();
        } catch {
            setError('crop');
        } finally {
            setIsApplying(false);
        }
    }, [
        cancelSelection,
        cropRect,
        cropSize,
        displayedSize,
        mode,
        onChange,
        onUpload,
        selection,
    ]);

    const cropNaturalSize = React.useMemo(
        () =>
            cropRect && selection
                ? getCropNaturalDimensions(
                      cropRect,
                      displayedSize,
                      selection.naturalSize,
                  )
                : EMPTY_SIZE,
        [cropRect, displayedSize, selection],
    );

    return {
        aspectRatio,
        cropNaturalSize,
        cropRect,
        error,
        isApplying,
        onApplyCrop,
        onCropPointerDown,
        onCropPointerMove,
        onCropPointerUp,
        onImageLoad,
        onRemove,
        onSelectFiles,
        selection,
    };
};
